import { useNavigate } from "@tanstack/react-router";
import { LogIn, LogOut, MoreHorizontal, Settings } from "lucide-react";
import { ROUTES } from "@/app/routes";
// reason: AttendanceStatusChip is a feature widget composed into the app shell -
// whether you are on the clock has to be answerable from every page, since the
// alternative is finding out days later that a day hardened into an absence.
// Same layering exception as NotificationsBell and CheckInCta in the topbar.
// architecture#A6 with PR8 justification.
import { AttendanceStatusChip } from "@/features/attendance/components/attendance-status-chip";
import { signOut } from "@/features/identity/identity.api";
import { resetAnalytics } from "@/shared/analytics/analytics";
import { useAuth } from "@/shared/auth/auth.context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { UserAvatar } from "@/shared/components/user-avatar";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import { useSidebarCollapsed } from "@/shared/hooks/use-sidebar-collapsed";
import { cn } from "@/shared/lib/cn";

/**
 * Sticky sidebar footer: authenticated user pill. Pill opens a dropdown menu
 * with sign out, clicking sign out hits /auth/signout and clears the cached
 * /auth/me query.
 *
 * When the user is anonymous, the pill becomes a "Sign in" CTA.
 */
export function SidebarFooter() {
  const { collapsed } = useSidebarCollapsed();
  return (
    <div
      className={cn(
        "flex flex-col gap-2 border-t border-border bg-surface-subtle",
        collapsed ? "items-center p-2" : "p-3",
      )}
    >
      {/* Above the user pill: it is about YOU, like the pill, and it is the
          one row here that changes during the day. */}
      <AttendanceStatusChip collapsed={collapsed} />
      <UserPill collapsed={collapsed} />
    </div>
  );
}

export interface SidebarRoleSubtitleInput {
  accountRole?: "owner" | "admin" | null;
  activeWorkspaceId?: string;
  memberships: ReadonlyArray<{
    workspaceId: string;
    role: "admin" | "manager" | "member";
  }>;
}

export function getSidebarRoleSubtitle({
  activeWorkspaceId,
  memberships,
}: SidebarRoleSubtitleInput): string {
  const activeRole = memberships.find(
    (membership) => membership.workspaceId === activeWorkspaceId,
  )?.role;

  if (activeRole === "admin") return "Admin";
  if (activeRole === "manager") return "Manager";
  return "Member";
}

function UserPill({ collapsed }: { collapsed: boolean }) {
  const auth = useAuth();
  const { workspace } = useActiveWorkspace();
  const navigate = useNavigate();

  if (!auth.isAuthenticated) {
    return (
      <button
        type="button"
        onClick={() => navigate({ to: ROUTES.auth.login.path, search: {} })}
        className={cn(
          "flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium",
          "outline-none transition-colors hover:bg-muted focus-visible:shadow-ring-focus",
          collapsed && "size-9 justify-center p-0",
        )}
        title={collapsed ? "Sign in" : undefined}
        aria-label={collapsed ? "Sign in" : undefined}
      >
        <LogIn className="size-3.5 text-text-tertiary" />
        {collapsed ? null : <span>Sign in</span>}
      </button>
    );
  }

  const user = auth.user;
  const subtitle = getSidebarRoleSubtitle({
    accountRole: user?.accountRole ?? null,
    activeWorkspaceId: workspace.id,
    memberships: auth.memberships,
  });

  async function onSignOut() {
    try {
      await signOut();
    } catch {
      // even on network error, fall through to the full reload below.
    }
    // Drop the analytics identity before the reload, otherwise the next
    // person to sign in on this browser inherits this one's distinct id.
    await resetAnalytics();
    // reason: hard reload (not SPA navigate) so all in-memory state, including
    // the cached /auth/me, is torn down. A client-side navigate left the next
    // user booting with the signed-out user's identity in the same tab.
    window.location.assign(ROUTES.auth.login.path);
  }

  const avatarWithStatus = (
    <div className="relative size-7">
      <UserAvatar
        displayName={user?.displayName ?? "?"}
        avatarUrl={user?.avatarUrl}
        className="size-7"
        fallbackClassName="bg-pulse text-3xs font-semibold text-white"
      />
      <span
        role="img"
        aria-label="Online"
        className="bg-success border-surface-subtle absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2"
      />
    </div>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${user?.displayName ?? "Account"}, account menu`}
          className={cn(
            "outline-none transition-colors focus-visible:shadow-ring-focus",
            collapsed
              ? "grid place-items-center rounded-md p-0.5 hover:bg-muted"
              : "grid w-full grid-cols-[28px_1fr_auto] items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-muted",
          )}
          title={collapsed ? `${user?.displayName ?? ""} · ${subtitle}` : undefined}
        >
          {avatarWithStatus}
          {collapsed ? null : (
            <>
              <div className="min-w-0 leading-tight">
                <div className="truncate text-xs font-semibold text-foreground">
                  {user?.displayName}
                </div>
                <div className="truncate font-mono text-3xs tracking-wide text-text-tertiary">
                  {subtitle}
                </div>
              </div>
              <span
                className="grid size-6 place-items-center rounded-sm text-text-tertiary"
                aria-hidden
              >
                <MoreHorizontal className="size-3.5" />
              </span>
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} className="w-56 p-1.5">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-2 font-mono text-3xs uppercase tracking-wider text-text-quaternary">
          Signed in as
        </DropdownMenuLabel>
        <div className="px-2.5 pb-2 pt-0.5">
          <div className="truncate text-sm font-medium">{user?.displayName}</div>
          <div className="truncate text-xs text-text-tertiary">{user?.email}</div>
        </div>
        <DropdownMenuSeparator className="my-1" />
        {/* One door. Settings owns Profile now (avatar, About/Role,
            password), so a second "Profile" entry beside it led to a
            near-identical screen and made the user choose between two
            things that sound the same. */}
        <DropdownMenuItem
          className="gap-2.5 px-2 py-1.5 text-xs text-text-secondary"
          onSelect={() => navigate({ to: ROUTES.settings.path })}
        >
          <Settings className="size-3.5 text-text-tertiary" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1" />
        <DropdownMenuItem
          className="gap-2.5 px-2 py-1.5 text-xs text-text-secondary"
          onSelect={onSignOut}
        >
          <LogOut className="size-3.5 text-text-tertiary" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
